import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdGroups, MdSchedule, MdSearch, MdSend, MdTextsms } from 'react-icons/md';
import { getData, postData } from '../../../Webservice/Webservice';
import { FONT, PhoneBubble, authToken, cleanMobile, fillTemplate, smsParts, validMobile } from './smsShared';

const EMPTY_FILTERS = { promotionType: 'sms', city: [], gender: [], status: [], customerType: [] };

const Section = ({ n, title, hint, accent, icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, color: '#0f172a', lineHeight: 1.2 }}>{n}. {title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{hint}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

const MultiSelect = ({ label, value, onChange, options, render }) => (
  <TextField select fullWidth size="small" label={label} value={value} SelectProps={{ multiple: true, renderValue: render || ((sel) => sel.join(', ')) }} onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}>
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        <Checkbox checked={value.includes(o.value)} size="small" />
        <ListItemText primary={o.label} />
      </MenuItem>
    ))}
  </TextField>
);

// `source` is an existing campaign being duplicated (pre-fills everything).
function SmsForm({ templates, onBack, onCreated, source = null }) {
  const isCopy = !!source;
  const [name, setName] = useState(source ? `${source.name} (copy)` : '');
  const [templateId, setTemplateId] = useState(source?.templateId?._id || '');
  const [values, setValues] = useState(source?.variableValues || {});
  const [mode, setMode] = useState('now'); // now | schedule
  const [scheduledAt, setScheduledAt] = useState('');

  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const firstLoad = useRef(false);

  const template = templates.find((t) => t._id === templateId);
  const message = template ? fillTemplate(template.content || '', values) : '';
  const size = smsParts(message);

  const fetchCustomers = async (f) => {
    setLoadingCustomers(true);
    try {
      const res = await postData('campaign/customer/list', f, authToken(), false);
      const unique = new Map();
      (res?.customers || []).forEach((c) => {
        if (!validMobile(c.mobile)) return;
        const key = cleanMobile(c.mobile);
        if (!unique.has(key)) unique.set(key, c);
      });
      const list = Array.from(unique.values());
      setCustomers(list);
      // a copy keeps the original recipients that are still reachable; otherwise everyone listed
      const original = !firstLoad.current && source?.customerIds ? new Set(source.customerIds.map(String)) : null;
      firstLoad.current = true;
      setSelected(original ? list.filter((c) => original.has(String(c._id))).map((c) => c._id) : list.map((c) => c._id));
    } catch (e) {
      setCustomers([]);
      setSelected([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    getData('campaign/city/list', null, authToken(), false).then((r) => setCities(r?.cities || [])).catch(() => setCities([]));
    fetchCustomers(EMPTY_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (key, value) => setFilters((p) => ({ ...p, [key]: value }));
  const pickTemplate = (id) => {
    const t = templates.find((x) => x._id === id);
    setTemplateId(id);
    setValues((prev) => (t?.variables || []).reduce((acc, v) => ({ ...acc, [v]: prev[v] || '' }), {}));
    setErrors((p) => ({ ...p, templateId: undefined }));
  };

  const filteredPicker = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return q ? customers.filter((c) => `${c.name || ''} ${c.mobile || ''}`.toLowerCase().includes(q)) : customers;
  }, [customers, pickerSearch]);

  const submit = async () => {
    const next = {};
    if (!name.trim()) next.name = 'Give the campaign a name';
    if (!templateId) next.templateId = 'Choose a template';
    const missing = (template?.variables || []).filter((v) => !String(values[v] || '').trim());
    if (missing.length) next.values = `Fill in: ${missing.join(', ')}`;
    if (mode === 'schedule') {
      if (!scheduledAt) next.scheduledAt = 'Choose when to send';
      else if (new Date(scheduledAt).getTime() < Date.now()) next.scheduledAt = 'That time has already passed';
    }
    if (!selected.length) next.audience = 'Select at least one customer';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      await postData('sms-campaign/create', {
        name: name.trim(),
        templateId,
        customerIds: selected,
        variableValues: values,
        scheduledAt: mode === 'schedule' ? new Date(scheduledAt).toISOString() : null,
      }, authToken());
      onCreated();
    } catch (e) {
      /* the shared service already showed the error */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1250, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton onClick={onBack} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>{isCopy ? 'Duplicate SMS Campaign' : 'New SMS Campaign'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{isCopy ? 'Everything is copied from the original. Change what you need, then send or schedule.' : 'Pick an approved template, fill in its values, choose the audience, then send or schedule.'}</Typography>
          </Box>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Section n={1} title="Campaign & template" hint="Only templates approved in MSG91 / DLT can be sent" accent="#6366f1" icon={MdTextsms}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Campaign name" required value={name} onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })); }} error={!!errors.name} helperText={errors.name || 'Only you see this'} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField select fullWidth size="small" label="Template" required value={templateId} onChange={(e) => pickTemplate(e.target.value)} error={!!errors.templateId} helperText={errors.templateId || (templates.length ? '' : 'No templates yet. Add one on the Templates tab.')}>
                      {templates.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                  {template && (template.variables || []).length > 0 && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5, mb: 1.25 }}>Fill in the template values</Typography>
                      <Grid container spacing={2}>
                        {template.variables.map((v) => (
                          <Grid item xs={12} md={4} key={v}>
                            <TextField fullWidth size="small" label={v} value={values[v] || ''} onChange={(e) => { setValues((p) => ({ ...p, [v]: e.target.value })); setErrors((p) => ({ ...p, values: undefined })); }} />
                          </Grid>
                        ))}
                      </Grid>
                      {errors.values && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 0.75 }}>{errors.values}</Typography>}
                      <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', mt: 1 }}>These values are the same for every recipient.</Typography>
                    </Grid>
                  )}
                </Grid>
              </Section>

              <Section n={2} title="Audience" hint="Customers with a valid 10-digit mobile number" accent="#0ea5e9" icon={MdGroups}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}><MultiSelect label="City" value={filters.city} onChange={(v) => setFilter('city', v)} options={cities.map((c) => ({ value: c, label: c }))} render={(sel) => (sel.length === cities.length && cities.length ? 'All cities' : sel.join(', '))} /></Grid>
                  <Grid item xs={12} md={6}><MultiSelect label="Gender" value={filters.gender} onChange={(v) => setFilter('gender', v)} options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]} /></Grid>
                  <Grid item xs={12} md={6}><MultiSelect label="Customer type" value={filters.customerType} onChange={(v) => setFilter('customerType', v)} options={[{ value: 'customer', label: 'Customer (B2C)' }, { value: 'business', label: 'Business (B2B)' }]} /></Grid>
                  <Grid item xs={12} md={6}><MultiSelect label="Account status" value={filters.status} onChange={(v) => setFilter('status', v)} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} /></Grid>
                </Grid>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                  <Button onClick={() => fetchCustomers(filters)} disabled={loadingCustomers} variant="outlined" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', color: '#0f172a', borderColor: '#cbd5e1' }}>{loadingCustomers ? 'Loading…' : 'Apply filters'}</Button>
                  <Button onClick={() => setPickerOpen(true)} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#e0f2fe', color: '#075985', px: 2 }}>{selected.length} of {customers.length} customers selected · review</Button>
                </Box>
                {errors.audience && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 1 }}>{errors.audience}</Typography>}
              </Section>

              <Section n={3} title="When to send" hint="Send right away or pick a date and time" accent="#10b981" icon={MdSchedule}>
                <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)} sx={{ gap: 1 }}>
                  {[['now', 'Send now', 'Starts as soon as you create it'], ['schedule', 'Schedule', 'Goes out by itself at the time you pick']].map(([v, label, sub]) => (
                    <Box key={v} onClick={() => setMode(v)} sx={{ flex: '1 1 220px', cursor: 'pointer', p: 1.25, borderRadius: '12px', border: `1.5px solid ${mode === v ? 'rgb(255 190 0)' : '#e2e8f0'}`, background: mode === v ? 'rgb(255 209 2 / 12%)' : '#fff' }}>
                      <FormControlLabel value={v} control={<Radio size="small" sx={{ color: 'rgb(255 190 0)', '&.Mui-checked': { color: 'rgb(255 190 0)' } }} />} label={<Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5 }}>{label}</Typography>} sx={{ m: 0 }} />
                      <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b', ml: 3.5 }}>{sub}</Typography>
                    </Box>
                  ))}
                </RadioGroup>
                {mode === 'schedule' && (
                  <TextField fullWidth size="small" type="datetime-local" label="Send at" InputLabelProps={{ shrink: true }} inputProps={{ min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) }} value={scheduledAt} onChange={(e) => { setScheduledAt(e.target.value); setErrors((p) => ({ ...p, scheduledAt: undefined })); }} error={!!errors.scheduledAt} helperText={errors.scheduledAt} sx={{ mt: 2 }} />
                )}
              </Section>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box sx={{ position: { lg: 'sticky' }, top: 16, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1.5 }}>Message preview</Typography>
                <PhoneBubble text={message} empty={template ? 'This template has no preview text. Add it under Templates.' : 'Choose a template to see the message'} />
                {message && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{size.length} characters{size.unicode ? ' · uses non-English characters' : ''}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 800, color: size.parts > 1 ? '#b45309' : '#059669' }}>{size.parts} SMS per person</Typography>
                  </Box>
                )}
                {message.includes('##') && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#b45309', mt: 1 }}>Some values are still empty (shown as ##name##).</Typography>}
              </Paper>

              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>Summary</Typography>
                {[
                  ['Recipients', selected.length.toLocaleString('en-IN')],
                  ['SMS to send', size.parts ? (selected.length * size.parts).toLocaleString('en-IN') : '-'],
                  ['Delivery', mode === 'now' ? 'Starts immediately' : scheduledAt ? new Date(scheduledAt).toLocaleString('en-IN') : 'Pick a time'],
                ].map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{k}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700 }}>{v}</Typography>
                  </Box>
                ))}
                <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#94a3b8', mt: 1 }}>SMS are billed by your provider per message part. Messages can't be recalled once sent.</Typography>
              </Paper>

              <Button onClick={submit} disabled={saving} variant="contained" startIcon={saving ? <CircularProgress size={18} sx={{ color: 'black' }} /> : mode === 'schedule' ? <MdSchedule /> : <MdSend />} sx={{ fontFamily: FONT, textTransform: 'none', background: 'rgb(255 209 2)', color: 'black', py: 1.4, borderRadius: '12px', fontWeight: 800, fontSize: 13.5, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>
                {saving ? 'Working…' : mode === 'schedule' ? 'Schedule campaign' : 'Create & send now'}
              </Button>
              <Button onClick={onBack} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Recipients ({selected.length} of {customers.length})</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" placeholder="Search name or mobile" value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} InputProps={{ startAdornment: <MdSearch style={{ marginRight: 8, color: '#94a3b8' }} /> }} sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button size="small" onClick={() => setSelected(customers.map((c) => c._id))} sx={{ textTransform: 'none', fontWeight: 700 }}>Select all</Button>
            <Button size="small" onClick={() => setSelected([])} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>
          </Box>
          <Box sx={{ maxHeight: 380, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
            {filteredPicker.length === 0 ? <Typography sx={{ p: 2, fontFamily: FONT, color: '#64748b' }}>No customers match.</Typography> : filteredPicker.map((c) => (
              <Box key={c._id} onClick={() => setSelected((p) => (p.includes(c._id) ? p.filter((x) => x !== c._id) : [...p, c._id]))} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', '&:hover': { background: '#f8fafc' } }}>
                <Checkbox checked={selected.includes(c._id)} size="small" disableRipple />
                <Box>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 12.5 }}>{c.name || 'Customer'}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{cleanMobile(c.mobile)}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPickerOpen(false)} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', '&:hover': { background: 'rgb(255 190 0)' } }}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SmsForm;
